import { z } from 'zod';

// General Settings Schema
export const generalSettingsSchema = z.object({
  site_name: z
    .string()
    .min(1, 'Site name is required')
    .max(100, 'Site name must be less than 100 characters'),
  site_description: z
    .string()
    .max(500, 'Site description must be less than 500 characters')
    .optional(),
  maintenance_mode: z.boolean(),
  maintenance_message: z
    .string()
    .max(1000, 'Maintenance message must be less than 1000 characters')
    .optional(),
  timezone: z.string().min(1, 'Timezone is required'),
  language: z.string().min(1, 'Language is required'),
  contact_email: z.string().email('Invalid email format').optional(),
  support_email: z.string().email('Invalid email format').optional(),
});

// Security Settings Schema
export const securitySettingsSchema = z.object({
  password_min_length: z
    .number()
    .min(6, 'Minimum password length is 6')
    .max(128, 'Maximum password length is 128'),
  password_require_uppercase: z.boolean(),
  password_require_lowercase: z.boolean(),
  password_require_numbers: z.boolean(),
  password_require_symbols: z.boolean(),
  session_timeout: z
    .number()
    .min(5, 'Minimum session timeout is 5 minutes')
    .max(1440, 'Maximum session timeout is 24 hours'),
  max_login_attempts: z
    .number()
    .min(1, 'Minimum login attempts is 1')
    .max(10, 'Maximum login attempts is 10'),
  lockout_duration: z
    .number()
    .min(1, 'Minimum lockout duration is 1 minute')
    .max(1440, 'Maximum lockout duration is 24 hours'),
  two_factor_enabled: z.boolean(),
  ip_whitelist: z
    .array(z.string().regex(/^(?:\d{1,3}\.){3}\d{1,3}$/, 'Invalid IP address'))
    .optional(),
});

// KYC Settings Schema
export const kycSettingsSchema = z.object({
  document_types: z
    .array(z.string())
    .min(1, 'At least one document type is required'),
  max_file_size: z
    .number()
    .min(1, 'Minimum file size is 1MB')
    .max(50, 'Maximum file size is 50MB'),
  allowed_file_types: z
    .array(z.string())
    .min(1, 'At least one file type is required'),
  auto_approve_threshold: z
    .number()
    .min(0, 'Threshold must be at least 0')
    .max(100, 'Threshold must be at most 100'),
  manual_review_required: z.boolean(),
  retention_period: z
    .number()
    .min(30, 'Minimum retention period is 30 days')
    .max(2555, 'Maximum retention period is 7 years'),
  face_detection_enabled: z.boolean(),
  liveness_detection_enabled: z.boolean(),
});

// Notification Settings Schema
export const notificationSettingsSchema = z.object({
  email_enabled: z.boolean(),
  sms_enabled: z.boolean(),
  push_enabled: z.boolean(),
  email_smtp_host: z
    .string()
    .min(1, 'SMTP host is required when email is enabled')
    .optional(),
  email_smtp_port: z
    .number()
    .min(1)
    .max(65535, 'Invalid port number')
    .optional(),
  email_smtp_username: z.string().optional(),
  email_smtp_password: z.string().optional(),
  email_from_address: z.string().email('Invalid email format').optional(),
  sms_provider: z.string().optional(),
  sms_api_key: z.string().optional(),
  notification_templates: z.record(z.any()).optional(),
});

// Performance Settings Schema
export const performanceSettingsSchema = z.object({
  cache_enabled: z.boolean(),
  cache_ttl: z
    .number()
    .min(60, 'Minimum cache TTL is 60 seconds')
    .max(86400, 'Maximum cache TTL is 24 hours'),
  api_rate_limit: z
    .number()
    .min(10, 'Minimum rate limit is 10')
    .max(10000, 'Maximum rate limit is 10,000'),
  max_concurrent_uploads: z
    .number()
    .min(1, 'Minimum concurrent uploads is 1')
    .max(20, 'Maximum concurrent uploads is 20'),
  image_processing_timeout: z
    .number()
    .min(30, 'Minimum timeout is 30 seconds')
    .max(600, 'Maximum timeout is 10 minutes'),
  database_connection_pool_size: z
    .number()
    .min(5, 'Minimum pool size is 5')
    .max(100, 'Maximum pool size is 100'),
  compression_enabled: z.boolean(),
});

// Combined Settings Schema (for full validation)
export const systemSettingsSchema = z.object({
  general: generalSettingsSchema,
  security: securitySettingsSchema,
  kyc: kycSettingsSchema,
  notifications: notificationSettingsSchema,
  performance: performanceSettingsSchema,
});

// Default values for each section
export const defaultGeneralSettings = {
  site_name: 'KYC Admin Portal',
  site_description: 'Identity Verification Management System',
  maintenance_mode: false,
  maintenance_message: 'System is under maintenance',
  timezone: 'Asia/Bangkok',
  language: 'en',
  contact_email: 'admin@example.com',
  support_email: 'support@example.com',
};

export const defaultSecuritySettings = {
  password_min_length: 8,
  password_require_uppercase: true,
  password_require_lowercase: true,
  password_require_numbers: true,
  password_require_symbols: true,
  session_timeout: 60,
  max_login_attempts: 5,
  lockout_duration: 15,
  two_factor_enabled: false,
  ip_whitelist: [],
};

export const defaultKycSettings = {
  document_types: ['national_id', 'passport', 'driver_license'],
  max_file_size: 10,
  allowed_file_types: ['jpg', 'jpeg', 'png', 'pdf'],
  auto_approve_threshold: 85,
  manual_review_required: true,
  retention_period: 2555,
  face_detection_enabled: true,
  liveness_detection_enabled: true,
};

export const defaultNotificationSettings = {
  email_enabled: true,
  sms_enabled: false,
  push_enabled: true,
  email_smtp_host: 'smtp.example.com',
  email_smtp_port: 587,
  email_smtp_username: '',
  email_smtp_password: '',
  email_from_address: 'noreply@example.com',
  sms_provider: 'twilio',
  sms_api_key: '',
  notification_templates: {},
};

export const defaultPerformanceSettings = {
  cache_enabled: true,
  cache_ttl: 3600,
  api_rate_limit: 1000,
  max_concurrent_uploads: 5,
  image_processing_timeout: 120,
  database_connection_pool_size: 20,
  compression_enabled: true,
};

// Helper function to get all default settings
export const getAllDefaultSettings = () => ({
  general: defaultGeneralSettings,
  security: defaultSecuritySettings,
  kyc: defaultKycSettings,
  notifications: defaultNotificationSettings,
  performance: defaultPerformanceSettings,
});

// Custom validation rules
export const validateEmailSettings = data => {
  const errors = [];

  if (data.email_enabled && !data.email_smtp_host) {
    errors.push('SMTP host is required when email notifications are enabled');
  }

  if (data.email_enabled && !data.email_from_address) {
    errors.push(
      'From address is required when email notifications are enabled'
    );
  }

  if (
    data.email_enabled &&
    data.email_smtp_port &&
    (data.email_smtp_port < 1 || data.email_smtp_port > 65535)
  ) {
    errors.push('SMTP port must be between 1 and 65535');
  }

  return errors;
};

export const validateSecuritySettings = data => {
  const errors = [];

  if (data.password_min_length < 6) {
    errors.push('Password minimum length must be at least 6 characters');
  }

  if (data.session_timeout < 5) {
    errors.push('Session timeout must be at least 5 minutes');
  }

  if (data.max_login_attempts < 1 || data.max_login_attempts > 10) {
    errors.push('Max login attempts must be between 1 and 10');
  }

  return errors;
};

export const validateKYCSettings = data => {
  const errors = [];

  if (data.document_types.length === 0) {
    errors.push('At least one document type must be selected');
  }

  if (data.allowed_file_types.length === 0) {
    errors.push('At least one file type must be allowed');
  }

  if (data.max_file_size < 1 || data.max_file_size > 50) {
    errors.push('Max file size must be between 1MB and 50MB');
  }

  if (data.auto_approve_threshold < 0 || data.auto_approve_threshold > 100) {
    errors.push('Auto approval threshold must be between 0 and 100');
  }

  return errors;
};

export const validatePerformanceSettings = data => {
  const errors = [];

  if (data.api_rate_limit < 10 || data.api_rate_limit > 10000) {
    errors.push(
      'API rate limit must be between 10 and 10,000 requests per minute'
    );
  }

  if (data.max_concurrent_uploads < 1 || data.max_concurrent_uploads > 20) {
    errors.push('Max concurrent uploads must be between 1 and 20');
  }

  if (
    data.image_processing_timeout < 30 ||
    data.image_processing_timeout > 600
  ) {
    errors.push(
      'Image processing timeout must be between 30 seconds and 10 minutes'
    );
  }

  if (
    data.database_connection_pool_size < 5 ||
    data.database_connection_pool_size > 100
  ) {
    errors.push('Database connection pool size must be between 5 and 100');
  }

  if (data.cache_enabled && (data.cache_ttl < 60 || data.cache_ttl > 86400)) {
    errors.push(
      'Cache TTL must be between 60 seconds and 24 hours when cache is enabled'
    );
  }

  return errors;
};

// Combined validation function
export const validateSystemSettings = settings => {
  const errors = {
    general: [],
    security: [],
    kyc: [],
    notifications: [],
    performance: [],
  };

  try {
    // Validate each category
    if (settings.general) {
      generalSettingsSchema.parse(settings.general);
    }

    if (settings.security) {
      securitySettingsSchema.parse(settings.security);
      errors.security = validateSecuritySettings(settings.security);
    }

    if (settings.kyc) {
      kycSettingsSchema.parse(settings.kyc);
      errors.kyc = validateKYCSettings(settings.kyc);
    }

    if (settings.notifications) {
      notificationSettingsSchema.parse(settings.notifications);
      errors.notifications = validateEmailSettings(settings.notifications);
    }

    if (settings.performance) {
      performanceSettingsSchema.parse(settings.performance);
      errors.performance = validatePerformanceSettings(settings.performance);
    }

    // Check if there are any errors
    const hasErrors = Object.values(errors).some(
      categoryErrors => categoryErrors.length > 0
    );

    return {
      valid: !hasErrors,
      errors: hasErrors ? errors : null,
    };
  } catch {
    return {
      valid: false,
      errors: {
        general: ['Validation failed'],
        security: [],
        kyc: [],
        notifications: [],
        performance: [],
      },
    };
  }
};
